import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  UserPlus, 
  Users, 
  Mail, 
  Trash2, 
  AlertCircle, 
  CheckCircle, 
  Crown,
  Shield,
  Code,
  Settings as SettingsIcon,
  UserMinus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Book,
  Network,
  Key,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Loader2,
  Cloud,
  Pencil,
  UsersRound,
  FileText,
  ShipWheel,
  Info,
} from 'lucide-react';
import { apiClient, InventoryNamespace, User, Invitation, OktaConfigResponse, OktaOIDCConfig, OktaSCIMConfig, CloudResourceOwnershipRequest, CloudResourceOwnershipMapping, UserGroup, UserGroupMember } from '../api/client';
import { useCloudResourceFilter } from '../hooks/useCloudResourceFilter';
import { CategoryFilterDropdown } from '../components/CategoryFilterDropdown';
import { UserAvatar } from '../components/UserAvatar';
import AuditLog from './AuditLog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../hooks/useAuth';

// SSO Setup Instructions Wizard
function SSOSetupWizard({ getRedirectUri, getSCIMBaseUrl, copyToClipboard }: {
  getRedirectUri: () => string;
  getSCIMBaseUrl: () => string;
  copyToClipboard: (text: string) => void;
}) {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: 'Configure OIDC',
      description: 'Set up OpenID Connect in Okta',
      content: (
        <div className="space-y-4">
          {/* Required URIs */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Sign-in Redirect URI</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate">{getRedirectUri()}</code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(getRedirectUri())}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          <ol className="space-y-2.5 text-sm text-muted-foreground">
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">1</span>
              <span>In Okta Admin Console, go to <strong className="text-foreground">Applications → Create App Integration</strong></span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">2</span>
              <span>Choose <strong className="text-foreground">OIDC - OpenID Connect</strong>, then <strong className="text-foreground">Web Application</strong></span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">3</span>
              <span>Set the <strong className="text-foreground">Sign-in redirect URI</strong> to the URI above</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">4</span>
              <span>Copy the <strong className="text-foreground">Client ID</strong> and <strong className="text-foreground">Client Secret</strong> from Okta into the OIDC configuration tab above</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">5</span>
              <span>Add your organization's email domains to the <strong className="text-foreground">Allowed Email Domains</strong> list</span>
            </li>
          </ol>
        </div>
      ),
    },
    {
      title: 'Configure SCIM',
      description: 'Enable user provisioning',
      content: (
        <div className="space-y-4">
          {/* SCIM Base URL */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">SCIM Base URL</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate">{getSCIMBaseUrl()}</code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(getSCIMBaseUrl())}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          <ol className="space-y-2.5 text-sm text-muted-foreground">
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">1</span>
              <span>In the SCIM tab above, click <strong className="text-foreground">Generate Token</strong> and save the configuration</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">2</span>
              <span>In Okta, go to <strong className="text-foreground">Applications → Browse App Catalog</strong> and search for <strong className="text-foreground">"SCIM 2.0 Test App (Header Auth)"</strong></span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">3</span>
              <span>Add the app and name it (e.g., "Kestrel AI SCIM Provisioning")</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">4</span>
              <span>Go to <strong className="text-foreground">Provisioning → Configure API Integration</strong>, enable it, and set the <strong className="text-foreground">Base URL</strong> and <strong className="text-foreground">API Token</strong> from above</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">5</span>
              <span>Click <strong className="text-foreground">Test API Credentials</strong> to verify the connection</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">6</span>
              <span>Go to <strong className="text-foreground">To App</strong> tab and enable: Create Users, Update User Attributes, Deactivate Users</span>
            </li>
          </ol>
        </div>
      ),
    },
    {
      title: 'Test Integration',
      description: 'Verify everything works',
      content: (
        <div className="space-y-4">
          <ol className="space-y-2.5 text-sm text-muted-foreground">
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">1</span>
              <span>Test SCIM provisioning by importing or assigning users in Okta</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">2</span>
              <span>Users can sign in using the <strong className="text-foreground">"Login with Okta"</strong> button on the login page</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">3</span>
              <span>Email domains must match the allowed domains configured in step 1</span>
            </li>
            <li className="flex gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">4</span>
              <span>First user from each tenant becomes <strong className="text-foreground">org_admin</strong>, subsequent users become <strong className="text-foreground">app_developer</strong></span>
            </li>
          </ol>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-700 dark:text-green-300">
              Once verified, your team can use Okta SSO to sign in to Kestrel.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-medium">Setup Instructions</CardTitle>
        <CardDescription>Follow these steps to configure Okta SSO for your organization</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Step indicators */}
        <div className="flex items-center gap-0 overflow-x-auto">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <button
                onClick={() => setActiveStep(index)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors text-left flex-1 flex-shrink-0 ${
                  activeStep === index
                    ? 'bg-primary/10 border border-primary/20'
                    : 'hover:bg-muted/50'
                }`}
              >
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  activeStep === index
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {index + 1}
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${activeStep === index ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{step.description}</p>
                </div>
              </button>
              {index < steps.length - 1 && (
                <div className="w-8 h-px bg-border flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>

        <Separator />

        {/* Active step content */}
        <div className="min-h-[200px]">
          {steps[activeStep].content}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
            disabled={activeStep === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
            disabled={activeStep === steps.length - 1}
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}




const TeamManagement: React.FC = () => {
  // Helper function to get the correct redirect URI based on environment
  const getRedirectUri = () => {
    if (window.location.hostname === 'staging-platform.usekestrel.ai') {
      return 'https://staging-platform.usekestrel.ai/auth/callback';
    } else if (window.location.hostname === 'platform.usekestrel.ai') {
      return 'https://platform.usekestrel.ai/auth/callback';
    } else {
      // Development environment
      return `${window.location.protocol}//${window.location.host}/auth/callback`;
    }
  };

  // Helper function to get the correct SCIM base URL based on environment
  const getSCIMBaseUrl = () => {
    if (window.location.hostname === 'staging-platform.usekestrel.ai') {
      return 'https://staging-platform.usekestrel.ai/scim/v2';
    } else if (window.location.hostname === 'platform.usekestrel.ai') {
      return 'https://platform.usekestrel.ai/scim/v2';
    } else {
      // Development environment
      return `${window.location.protocol}//${window.location.host}/scim/v2`;
    }
  };

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'app_developer' | 'ops_admin'>('app_developer');

  // Namespace assignment state
  const [namespaces, setNamespaces] = useState<InventoryNamespace[]>([]);
  const [namespaceAssignments, setNamespaceAssignments] = useState<Record<string, string[]>>({});
  const [ownershipRequests, setOwnershipRequests] = useState<any[]>([]);

  // Cloud resource ownership state
  const cloudFilter = useCloudResourceFilter({
    pageSize: 10000,
    enableVPCs: true,
  });
  const [cloudResourceAssignments, setCloudResourceAssignments] = useState<Record<string, CloudResourceOwnershipMapping[]>>({});
  const [cloudResourceOwnershipRequests, setCloudResourceOwnershipRequests] = useState<CloudResourceOwnershipRequest[]>([]);
  const [selectedDeveloperForCloudResources, setSelectedDeveloperForCloudResources] = useState<string | null>(null);
  const [cloudDeveloperSearchTerm, setCloudDeveloperSearchTerm] = useState('');
  const [collapsedVpcs, setCollapsedVpcs] = useState<Set<string>>(new Set());

  // Remove user dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{id: string, email: string} | null>(null);

  // Role editing state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<{id: string, email: string, currentRole: string} | null>(null);
  const [newRole, setNewRole] = useState<'app_developer' | 'ops_admin' | 'org_admin'>('app_developer');

  // Tab state
  const [activeMainTab, setActiveMainTab] = useState<'invitations' | 'members' | 'user-groups' | 'access-control' | 'np-admin' | 'sso' | 'audit'>('invitations');
  const [activeNpAdminTab, setActiveNpAdminTab] = useState<'namespaces' | 'requests' | 'cloud-resources'>('namespaces');

  // Subscription-based tab visibility
  const { isProductBilling, access } = useSubscription();
  const { user: authUser } = useAuth();
  // Resource Ownership only routes incident notifications, so it's hidden with
  // the legacy Incidents product unless the tenant has it re-enabled.
  const legacyProductsEnabled = authUser?.legacy_products_enabled === true;
  const showResourceOwnership = legacyProductsEnabled && (!isProductBilling || access.incident_response);

  useEffect(() => {
    if (activeMainTab === 'np-admin' && !showResourceOwnership) {
      setActiveMainTab('invitations');
    }
  }, [showResourceOwnership, activeMainTab]);
  
  // Okta SSO state
  const [oktaConfig, setOktaConfig] = useState<OktaConfigResponse | null>(null);
  const [loadingOkta, setLoadingOkta] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showSCIMToken, setShowSCIMToken] = useState(false);
  const [isEditingOIDC, setIsEditingOIDC] = useState(false);
  const [isEditingSCIM, setIsEditingSCIM] = useState(false);
  const [oidcConfig, setOidcConfig] = useState<OktaOIDCConfig>({
    okta_domain: '',
    client_id: '',
    client_secret: '',
    allowed_domains: []
  });
  const [scimConfig, setScimConfig] = useState<OktaSCIMConfig>({
    okta_domain: '',
    scim_app_name: '',
    scim_token: ''
  });
  const [newDomain, setNewDomain] = useState('');

  // Namespace assignment UI state
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(null);
  const [namespaceSearchTerm, setNamespaceSearchTerm] = useState('');
  const [debouncedNamespaceSearch, setDebouncedNamespaceSearch] = useState('');
  const [developerSearchTerm, setDeveloperSearchTerm] = useState('');
  const [developerCurrentPage, setDeveloperCurrentPage] = useState(1);
  const [developersPerPage] = useState(12);
  const [namespacePage, setNamespacePage] = useState(1);
  const [namespacesPerPage] = useState(30);
  const [namespaceTotalCount, setNamespaceTotalCount] = useState(0);
  const [namespaceTotalPages, setNamespaceTotalPages] = useState(1);
  const [namespacesLoading, setNamespacesLoading] = useState(false);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(10);

  // Initial data fetch - other effects handle pagination/search changes
  useEffect(() => {
    fetchUsers();
    fetchInvitations();
    fetchOwnershipRequests();
    fetchNamespaceAssignments();
    fetchCloudResourceOwnershipRequests();
    fetchCloudResourceAssignments();
    cloudFilter.refetchVPCs();
    fetchOktaConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.listTenantUsers();
      setUsers(response.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await apiClient.listInvitations();
      setInvitations(response.invitations || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  const fetchNamespaces = useCallback(async (page: number = 1, search: string = '') => {
    setNamespacesLoading(true);
    try {
      const response = await apiClient.getInventoryNamespacesPaginated({
        page,
        page_size: namespacesPerPage,
        search: search || undefined,
      });
      setNamespaces(response?.namespaces || []);
      setNamespaceTotalCount(response?.total_count || 0);
      setNamespaceTotalPages(response?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch namespaces:', err);
    } finally {
      setNamespacesLoading(false);
    }
  }, [namespacesPerPage]);

  const fetchOwnershipRequests = async () => {
    try {
      const response = await apiClient.listNamespaceOwnershipRequests();
      setOwnershipRequests(response.requests || []);
    } catch(err) {
      console.error("failed to fetch ownership requests")
    }
  }

  const fetchNamespaceAssignments = async () => {
    try {
      const response = await apiClient.getAllNamespaceOwnershipMappings();
      if (response.assignments) {
        const assignments: Record<string, string[]> = {};
        for (const assignment of response.assignments) {
          if (!assignments[assignment.user_id]) {
            assignments[assignment.user_id] = [];
          }
          assignments[assignment.user_id].push(assignment.namespace_id);
        }
        setNamespaceAssignments(assignments);
      }
    } catch (err) {
      console.error('Failed to fetch namespace assignments:', err);
    }
  };

  // Debounce namespace search (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNamespaceSearch(namespaceSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [namespaceSearchTerm]);

  // Refetch namespaces when page or debounced search changes
  useEffect(() => {
    fetchNamespaces(namespacePage, debouncedNamespaceSearch);
  }, [namespacePage, debouncedNamespaceSearch, fetchNamespaces]);

  const fetchCloudResourceOwnershipRequests = async () => {
    try {
      const response = await apiClient.listCloudResourceOwnershipRequests();
      setCloudResourceOwnershipRequests(response.requests || []);
    } catch (err) {
      console.error('Failed to fetch cloud resource ownership requests:', err);
    }
  };

  const fetchCloudResourceAssignments = async () => {
    try {
      const response = await apiClient.getAllCloudResourceOwnershipMappings();
      const assignments: Record<string, CloudResourceOwnershipMapping[]> = {};
      if (response.assignments) {
        for (const assignment of response.assignments) {
          if (!assignments[assignment.user_id]) {
            assignments[assignment.user_id] = [];
          }
          assignments[assignment.user_id].push(assignment);
        }
      }
      setCloudResourceAssignments(assignments);
    } catch (err) {
      console.error('Failed to fetch cloud resource assignments:', err);
    }
  };

  const handleApproveCloudResourceOwnership = async (requestId: string) => {
    try {
      await apiClient.approveCloudResourceOwnershipRequest({ request_id: requestId });
      await Promise.all([
        fetchCloudResourceOwnershipRequests(),
        fetchCloudResourceAssignments()
      ]);
    } catch (err) {
      console.error('Failed to approve cloud resource ownership request:', err);
    }
  };

  const handleRejectCloudResourceOwnership = async (requestId: string) => {
    try {
      await apiClient.rejectCloudResourceOwnershipRequest({ request_id: requestId });
      await fetchCloudResourceOwnershipRequests();
    } catch (err) {
      console.error('Failed to reject cloud resource ownership request:', err);
    }
  };

  const handleAssignCloudResource = async (userId: string, cloudResourceId: string) => {
    const resource = cloudFilter.resources.find(r => r.id === cloudResourceId);
    try {
      await apiClient.assignCloudResourceOwner({ user_id: userId, cloud_resource_id: cloudResourceId });
      setCloudResourceAssignments(prev => ({
        ...prev,
        [userId]: [...(prev[userId] || []), {
          user_id: userId,
          cloud_resource_id: cloudResourceId,
          resource_name: resource?.name,
          resource_type: resource?.resource_type,
          resource_arn: resource?.arn,
          cloud_provider: resource?.cloud_provider,
        }]
      }));
      setSuccess('Cloud resource assigned successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to assign cloud resource');
    }
  };

  const handleRemoveCloudResourceAssignment = async (userId: string, cloudResourceId: string) => {
    try {
      await apiClient.removeCloudResourceOwner({ user_id: userId, cloud_resource_id: cloudResourceId });
      setCloudResourceAssignments(prev => ({
        ...prev,
        [userId]: (prev[userId] || []).filter(m => m.cloud_resource_id !== cloudResourceId)
      }));
      setSuccess('Cloud resource removed successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to remove cloud resource');
    }
  };

  const handleAssignVPC = async (userId: string, vpcId: string) => {
    try {
      await apiClient.assignCloudResourceOwnerByVPC({ user_id: userId, vpc_id: vpcId });
      // VPC assigns many child resources — refetch to get accurate list
      await fetchCloudResourceAssignments();
      setSuccess('VPC resources assigned successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to assign VPC resources');
    }
  };

  const handleRemoveVPC = async (userId: string, vpcId: string) => {
    try {
      await apiClient.removeCloudResourceOwnerByVPC({ user_id: userId, vpc_id: vpcId });
      // Optimistic update: remove all mappings for this VPC from the user
      setCloudResourceAssignments(prev => ({
        ...prev,
        [userId]: (prev[userId] || []).filter(m => m.vpc_id !== vpcId)
      }));
      setSuccess('VPC resources removed successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to remove VPC resources');
    }
  };

  const fetchOktaConfig = async () => {
    setLoadingOkta(true);
    try {
      const response = await apiClient.getOktaConfig();
      setOktaConfig(response);
      if (response.oidc_app) {
        setOidcConfig(response.oidc_app);
      }
      if (response.scim_app) {
        setScimConfig(response.scim_app);
      }
    } catch (err) {
      console.error('Failed to fetch Okta configuration:', err);
    } finally {
      setLoadingOkta(false);
    }
  };




  const handleSaveOIDCConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const configToSave = {
        ...oidcConfig,
        allowed_domains: oidcConfig.allowed_domains.filter(d => d.length > 0)
      };
      
      if (oktaConfig?.oidc_app) {
        await apiClient.updateOktaOIDCConfig(configToSave);
        setSuccess('OIDC configuration updated successfully');
      } else {
        await apiClient.createOktaOIDCConfig(configToSave);
        setSuccess('OIDC configuration created successfully');
      }
      setIsEditingOIDC(false);
      await fetchOktaConfig();
    } catch (err: any) {
      setError(err.message || 'Failed to save OIDC configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSCIMConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (oktaConfig?.scim_app) {
        await apiClient.updateOktaSCIMConfig(scimConfig);
        setSuccess('SCIM configuration updated successfully');
      } else {
        await apiClient.createOktaSCIMConfig(scimConfig);
        setSuccess('SCIM configuration created successfully');
      }
      setIsEditingSCIM(false);
      await fetchOktaConfig();
    } catch (err: any) {
      setError(err.message || 'Failed to save SCIM configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = () => {
    if (newDomain && !oidcConfig.allowed_domains.includes(newDomain)) {
      setOidcConfig({
        ...oidcConfig,
        allowed_domains: [...oidcConfig.allowed_domains, newDomain]
      });
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setOidcConfig({
      ...oidcConfig,
      allowed_domains: oidcConfig.allowed_domains.filter(d => d !== domain)
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  const generateBearerToken = () => {
    // Generate a secure random token (64 characters hex)
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    // Add Bearer prefix for SCIM 2.0 compatibility
    setScimConfig({ ...scimConfig, scim_token: `Bearer ${token}` });
  };

  const handleDeleteOIDCConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.deleteOktaOIDCConfig();
      setSuccess('OIDC configuration deleted successfully');
      setOktaConfig(prev => prev ? { ...prev, oidc_app: undefined } : null);
      setOidcConfig({
        okta_domain: '',
        client_id: '',
        client_secret: '',
        allowed_domains: []
      });
    } catch (err: any) {
      setError(err.message || 'Failed to delete OIDC configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSCIMConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.deleteOktaSCIMConfig();
      setSuccess('SCIM configuration deleted successfully');
      setOktaConfig(prev => prev ? { ...prev, scim_app: undefined } : null);
      setScimConfig({
        okta_domain: '',
        scim_app_name: '',
        scim_token: ''
      });
    } catch (err: any) {
      setError(err.message || 'Failed to delete SCIM configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiClient.inviteUser({ email: inviteEmail, role: inviteRole });
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      fetchInvitations();
    } catch (err: any) {
      // Handle specific error cases for better UX
      if (err.response?.status === 409 || err.message?.includes('already invited')) {
        setError('The user you are trying to invite is already onboarded onto the tenant');
      } else {
        setError(err.message || 'Failed to send invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await apiClient.revokeInvitation(invitationId);
      setSuccess('Invitation revoked successfully');
      fetchInvitations();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke invitation');
    }
  };

  const handleRemoveUser = (userId: string, userEmail: string) => {
    setUserToRemove({ id: userId, email: userEmail });
    setRemoveDialogOpen(true);
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove) return;

    try {
      await apiClient.removeUserFromOrg(userToRemove.id);
      setSuccess(`User ${userToRemove.email} has been removed from the organization`);
      fetchUsers(); // Refresh the user list
      setRemoveDialogOpen(false);
      setUserToRemove(null);
    } catch (error: any) {
      // Safely extract a string error message from response
      let errorMessage = 'Failed to remove user';
      const data = error.response?.data;
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (data) {
        try {
          errorMessage = JSON.stringify(data);
        } catch {
          // Keep default fallback
        }
      }
      setError(errorMessage);
      console.error('Error removing user:', error);
    }
  };

  const handleEditRole = (userId: string, userEmail: string, currentRole: string) => {
    setUserToEdit({ id: userId, email: userEmail, currentRole });
    setNewRole(currentRole as 'app_developer' | 'ops_admin' | 'org_admin');
    setRoleDialogOpen(true);
  };

  const confirmRoleChange = async () => {
    if (!userToEdit) return;

    setLoading(true);
    try {
      await apiClient.updateUserRole({ user_id: userToEdit.id, role: newRole });
      setSuccess(`Successfully updated ${userToEdit.email}'s role to ${formatRole(newRole)}`);
      fetchUsers(); // Refresh the user list
      setRoleDialogOpen(false);
      setUserToEdit(null);
    } catch (error: any) {
      // Safely extract a string error message from response
      let errorMessage = 'Failed to update user role';
      const data = error.response?.data;
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (data) {
        try {
          errorMessage = JSON.stringify(data);
        } catch {
          // Keep default fallback
        }
      }
      setError(errorMessage);
      console.error('Error updating user role:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      await apiClient.approveNamespaceOwnershipRequest({ request_id: requestId });
      setSuccess('Ownership request approved successfully!');
      
      // Refetch both requests and assignments to update the entire view
      await Promise.all([
        fetchOwnershipRequests(),
        fetchNamespaceAssignments()
      ]);
    } catch (err: any) {
      setError(err.message || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await apiClient.rejectNamespaceOwnershipRequest({ request_id: requestId });
      setSuccess('Ownership request rejected successfully!');
      // Refetch requests to update the list
      await fetchOwnershipRequests();
    } catch (err: any) {
      setError(err.message || 'Failed to reject request');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'org_admin': return <Crown className="h-4 w-4" />;
      case 'ops_admin': return <Shield className="h-4 w-4" />;
      case 'app_developer': return <Code className="h-4 w-4" />;
      default: return <SettingsIcon className="h-4 w-4" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'org_admin': return 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
      case 'ops_admin': return 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
      case 'app_developer': return 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
      default: return 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700';
    }
  };

  const formatRole = (role: string) => {
    if (role === 'ops_admin') return 'Platform Admin';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    // Use a consistent format: MM/DD/YYYY
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const assignNamespaceToUser = async (userId: string, namespaceId: string) => {
    try {
      await apiClient.assignNamespaceOwner({ user_id: userId, namespace_id: namespaceId });
      setNamespaceAssignments(prev => ({
        ...prev,
        [userId]: [...(prev[userId] || []), namespaceId]
      }));
      setSuccess('Namespace assigned successfully!');
    } catch (error: any) {
      setError(error.message || 'Failed to assign namespace');
    }
  };
  
  const removeNamespaceFromUser = async (userId: string, namespaceId: string) => {
    try {
      await apiClient.removeNamespaceOwner({ user_id: userId, namespace_id: namespaceId });
      setNamespaceAssignments(prev => ({
        ...prev,
        [userId]: (prev[userId] || []).filter(id => id !== namespaceId)
      }));
      setSuccess('Namespace removed successfully!');
    } catch (error: any) {
      setError(error.message || 'Failed to remove namespace');
    }
  };

  // App developers and ops admins can own namespaces and cloud resources
  const assignableUsers = users.filter(user => user.role === 'app_developer' || user.role === 'ops_admin');

  // Filter and paginate app developers
  const filteredDevelopers = assignableUsers.filter(dev =>
    dev.email.toLowerCase().includes(developerSearchTerm.toLowerCase())
  );

  const developerStartIndex = (developerCurrentPage - 1) * developersPerPage;
  const developerEndIndex = developerStartIndex + developersPerPage;
  const paginatedDevelopers = filteredDevelopers.slice(developerStartIndex, developerEndIndex);

  // Filter developers for cloud resources section
  const filteredCloudDevelopers = assignableUsers.filter(dev =>
    dev.email.toLowerCase().includes(cloudDeveloperSearchTerm.toLowerCase())
  );

  // Filter out already-assigned cloud resources and VPC/VCN types when VPC groups are shown
  const VPC_RESOURCE_TYPES = new Set(['AWS::EC2::VPC', 'OCI::Core::VCN']);
  const currentUserAssignments = cloudResourceAssignments[selectedDeveloperForCloudResources || ''] || [];
  const assignedVpcIds = new Set(currentUserAssignments.filter(m => m.vpc_id).map(m => m.vpc_id));
  const availableCloudResources = cloudFilter.resources.filter(res =>
    !currentUserAssignments.some(m => m.cloud_resource_id === res.id) &&
    !(cloudFilter.showVpcGroups && VPC_RESOURCE_TYPES.has(res.resource_type))
  );
  const availableVpcs = cloudFilter.vpcs.filter(vpc => !assignedVpcIds.has(vpc.vpc_id));


  useEffect(() => {
    if (assignableUsers.length > 0 && !selectedDeveloper) {
      setSelectedDeveloper(assignableUsers[0].user_id);
    }
  }, [assignableUsers, selectedDeveloper]);

  // Auto-select first developer for cloud resources
  useEffect(() => {
    if (assignableUsers.length > 0 && !selectedDeveloperForCloudResources) {
      setSelectedDeveloperForCloudResources(assignableUsers[0].user_id);
    }
  }, [assignableUsers, selectedDeveloperForCloudResources]);

  const handleDeveloperSearchChange = (value: string) => {
    setDeveloperSearchTerm(value);
    setDeveloperCurrentPage(1);
  };

  // Filter out already-assigned namespaces (search is done server-side)
  const availableNamespaces = namespaces.filter(ns =>
    !(namespaceAssignments[selectedDeveloper || ''] || []).includes(ns.id)
  );

  const handleNamespaceSearchChange = (value: string) => {
    setNamespaceSearchTerm(value);
    setNamespacePage(1); // Reset to first page when search changes
  };

  // Filter and paginate users
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <AlertDescription className="mb-0">{error}</AlertDescription>
            </div>
          </Alert>
        )}
        
        {success && (
          <Alert className="border-green-200 bg-green-50 dark:border-success/30 dark:bg-success/20">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-success-foreground mr-2 flex-shrink-0" />
              <AlertDescription className="text-green-700 dark:text-success-foreground mb-0">
                {success}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="border-b border-border">
          <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveMainTab('invitations')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'invitations'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Invitations & Onboarding
              </div>
            </button>
            <button
              onClick={() => setActiveMainTab('members')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'members'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Team Members ({users.length})
              </div>
            </button>
            <button
              onClick={() => setActiveMainTab('user-groups')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'user-groups'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <UsersRound className="h-4 w-4" />
                User Groups
              </div>
            </button>
            <button
              onClick={() => setActiveMainTab('access-control')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'access-control'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Access Control
              </div>
            </button>
            {showResourceOwnership && (
            <button
              onClick={() => setActiveMainTab('np-admin')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'np-admin'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4" />
                Resource Ownership
              </div>
            </button>
            )}
            <button
              onClick={() => setActiveMainTab('sso')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'sso'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                SSO Configuration
              </div>
            </button>
            <button
              onClick={() => setActiveMainTab('audit')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeMainTab === 'audit'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Audit Log
              </div>
            </button>
          </nav>
        </div>

        <div>
          {activeMainTab === 'invitations' && (
            <div className="space-y-6">
              <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">
                  Invite Team Members
                </CardTitle>
                <CardDescription>
                  Send invitations to add new members to your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInviteUser} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="inviteEmail">Email Address</Label>
                      <Input
                        id="inviteEmail"
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required
                        className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="inviteRole">Role</Label>
                      <Select 
                        value={inviteRole} 
                        onValueChange={(value) => setInviteRole(value as 'app_developer' | 'ops_admin')}
                      >
                        <SelectTrigger id="inviteRole">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="app_developer">
                            <div className="flex items-center gap-2">
                              <Code className="h-4 w-4" />
                              App Developer
                            </div>
                          </SelectItem>
                          <SelectItem value="ops_admin">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              Platform Admin
                            </div>
                          </SelectItem>
                          <SelectItem value="org_admin">
                            <div className="flex items-center gap-2">
                              <Crown className="h-4 w-4" />
                              Organization Admin
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? 'Sending...' : 'Send Invitation'}
                    <Mail className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  Pending Invitations
                  <Badge variant="secondary" className="ml-auto">
                    {invitations.length}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Invitations that haven't been accepted yet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invitations.map((invitation) => (
                    <div key={invitation.invitation_id} className="flex items-center justify-between gap-2 p-3 border rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{invitation.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Invited {formatDate(invitation.created_at)} • Expires {formatDate(invitation.expires_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className={getRoleBadgeColor(invitation.role)}>
                          {formatRole(invitation.role)}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeInvitation(invitation.invitation_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {invitations.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No pending invitations</p>
                  )}
                </div>
              </CardContent>
            </Card>
            </div>
          )}

          {activeMainTab === 'members' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    Team Members
                    <Badge variant="secondary" className="ml-auto">
                      {users.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Current members of your organization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search team members..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="space-y-3">
                    {paginatedUsers.map((user) => (
                      <div key={user.user_id} className="group flex flex-col sm:flex-row sm:items-center p-4 border rounded-lg hover:bg-accent/50 transition-colors gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {user.picture_url ? (
                            <UserAvatar email={user.email} pictureUrl={user.picture_url} givenName={user.given_name} familyName={user.family_name} size="lg" />
                          ) : (
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              {getRoleIcon(user.role)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" title={user.email}>{user.email}</p>
                            <p className="text-sm text-muted-foreground">
                              Joined {formatDate(user.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 sm:ml-4 ml-[52px]">
                          <Badge className={getRoleBadgeColor(user.role)}>
                            {formatRole(user.role)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditRole(user.user_id, user.email, user.role)}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted"
                            title="Edit role"
                          >
                            <SettingsIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUser(user.user_id, user.email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Remove user"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {filteredUsers.length === 0 && searchTerm && (
                      <div className="text-center py-8">
                        <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No team members found matching "{searchTerm}"</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSearchTerm('')}
                          className="mt-2"
                        >
                          Clear search
                        </Button>
                      </div>
                    )}
                    
                    {users.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No team members found</p>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-6 pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} members
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>

                        <div className="hidden sm:flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                className="w-8 h-8 p-0"
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <span className="sm:hidden text-sm text-muted-foreground whitespace-nowrap">
                          Page {currentPage} of {totalPages}
                        </span>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeMainTab === 'user-groups' && (
            <UserGroupsTab users={users} />
          )}

          {activeMainTab === 'access-control' && (
            <AccessControlTab />
          )}

          {activeMainTab === 'np-admin' && showResourceOwnership && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium">Resource Ownership</CardTitle>
                  <CardDescription>
                    Manage namespace ownership and ownership requests for Kubernetes resources for incident notification routing.
                  </CardDescription>
                </CardHeader>
              </Card>

              <div className="border-b border-border">
                <nav className="flex space-x-2 sm:space-x-8 -mb-px px-4 md:px-6 overflow-x-auto">
                  <button
                    onClick={() => setActiveNpAdminTab('namespaces')}
                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeNpAdminTab === 'namespaces'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ShipWheel className="h-5 w-5" />
                      Namespace Ownership
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveNpAdminTab('cloud-resources')}
                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeNpAdminTab === 'cloud-resources'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Cloud className="h-5 w-5" />
                      Cloud Ownership
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveNpAdminTab('requests')}
                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeNpAdminTab === 'requests'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Ownership Requests
                      {(ownershipRequests.length + cloudResourceOwnershipRequests.length) > 0 && (
                        <Badge className="ml-2">{ownershipRequests.length + cloudResourceOwnershipRequests.length}</Badge>
                      )}
                    </div>
                  </button>
                </nav>
              </div>

              <div className="px-4 md:px-6">
                {activeNpAdminTab === 'namespaces' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base font-medium">
                          Assign Namespaces to Users
                        </CardTitle>
                        <CardDescription>
                          First select a user, then select namespaces to assign to them.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-6">
                        {assignableUsers.length === 0 ? (
                          <div className="text-center py-8">
                            <p className="text-muted-foreground">No assignable users found</p>
                            <p className="text-sm text-muted-foreground">Invite users with the App Developer or Platform Admin role to start assigning namespaces.</p>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm flex items-center gap-2">
                                  Select User
                                </h4>
                                <Badge variant="secondary" className="text-xs">
                                  {filteredDevelopers.length} of {assignableUsers.length} users
                                </Badge>
                              </div>

                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search developers..."
                                  value={developerSearchTerm}
                                  onChange={(e) => handleDeveloperSearchChange(e.target.value)}
                                  className="pl-10"
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {paginatedDevelopers.map((dev) => (
                                  <div
                                    key={dev.user_id}
                                    onClick={() => setSelectedDeveloper(dev.user_id)}
                                    className={`group cursor-pointer p-4 border-2 rounded-lg transition-all ${
                                      selectedDeveloper === dev.user_id
                                        ? 'border-primary bg-primary/5 shadow-md'
                                        : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {dev.picture_url ? (
                                        <UserAvatar email={dev.email} pictureUrl={dev.picture_url} givenName={dev.given_name} familyName={dev.family_name} size="lg" />
                                      ) : (
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                          selectedDeveloper === dev.user_id
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground'
                                        }`}>
                                          <Code className="h-5 w-5" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium break-words text-sm" title={dev.email}>{dev.email}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {(namespaceAssignments[dev.user_id] || []).length} namespaces
                                        </p>
                                      </div>
                                      {selectedDeveloper === dev.user_id && (
                                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {selectedDeveloper && (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6 border-t">
                                <div className="space-y-4">
                                  <h4 className="font-medium text-sm">Available Namespaces</h4>
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                      placeholder="Search namespaces..."
                                      value={namespaceSearchTerm}
                                      onChange={(e) => handleNamespaceSearchChange(e.target.value)}
                                      className="pl-10"
                                    />
                                  </div>

                                  {/* Pagination info */}
                                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                                    <span>
                                      {namespacesLoading ? 'Loading...' : `Showing ${availableNamespaces.length} of ${namespaceTotalCount} namespaces (page ${namespacePage})`}
                                    </span>
                                    {namespaceTotalPages > 1 && (
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setNamespacePage(p => Math.max(1, p - 1))}
                                          disabled={namespacePage === 1 || namespacesLoading}
                                        >
                                          <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span>Page {namespacePage} of {namespaceTotalPages}</span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setNamespacePage(p => Math.min(namespaceTotalPages, p + 1))}
                                          disabled={namespacePage === namespaceTotalPages || namespacesLoading}
                                        >
                                          <ChevronRight className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto p-2 border rounded-lg bg-muted/20">
                                    {availableNamespaces.map(ns => (
                                      <div
                                        key={ns.id}
                                        onClick={() => assignNamespaceToUser(selectedDeveloper, ns.id)}
                                        className="group relative p-3 border-2 border-dashed border-primary/30 hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm rounded-lg transition-all cursor-pointer"
                                      >
                                        <div>
                                          <p className="font-medium break-words">{ns.name}</p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Cluster: {ns.cluster_name || 'Unknown'}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                    {!namespacesLoading && availableNamespaces.length === 0 && (
                                      <p className="text-muted-foreground text-center py-4">No namespaces found matching your search.</p>
                                    )}
                                  </div>
                                </div>

                                <div className="space-y-4">
                                  <h4 className="font-medium text-sm">
                                    {users.find(u => u.user_id === selectedDeveloper)?.email}'s Namespaces
                                  </h4>
                                  <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {(namespaceAssignments[selectedDeveloper] || []).length > 0 ? (
                                      <div className="space-y-2">
                                        {(namespaceAssignments[selectedDeveloper] || []).map(nsId => {
                                          const namespace = namespaces.find(ns => ns.id === nsId);
                                          if (!namespace) return null;
                                          return (
                                            <div key={nsId} className="group flex items-center justify-between p-3 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-sm transition-shadow">
                                              <div className="flex-1 min-w-0">
                                                <p className="font-medium break-words">{namespace.name}</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                  Cluster: {namespace.cluster_name || 'Unknown'}
                                                </p>
                                              </div>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeNamespaceFromUser(selectedDeveloper, nsId)}
                                                className="sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0 ml-3"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="text-center py-8 text-muted-foreground">
                                        <p className="text-sm">No namespaces assigned yet</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
                
                {activeNpAdminTab === 'requests' && (
                  <div className="space-y-6">
                    {/* Namespace Ownership Requests */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base font-medium">
                          Namespace Ownership Requests
                          {ownershipRequests.length > 0 && (
                            <Badge className="ml-2">{ownershipRequests.length}</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Approve or reject requests from developers to own namespaces.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {ownershipRequests.map((request) => (
                            <div key={request.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-lg">
                              <div className="min-w-0">
                                <p className="font-medium">{request.user_email}</p>
                                <p className="text-sm text-muted-foreground">
                                  wants to own <strong>{request.namespace_name}</strong> in <strong>{request.cluster_name || 'Unknown Cluster'}</strong>
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Requested on {formatDate(request.created_at)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button size="sm" onClick={() => handleApproveRequest(request.id)}>Approve</Button>
                                <Button size="sm" variant="destructive" onClick={() => handleRejectRequest(request.id)}>Reject</Button>
                              </div>
                            </div>
                          ))}
                          {ownershipRequests.length === 0 && (
                            <p className="text-muted-foreground text-center py-4">No pending namespace ownership requests.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Cloud Resource Ownership Requests */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base font-medium">
                          Cloud Resource Ownership Requests
                          {cloudResourceOwnershipRequests.length > 0 && (
                            <Badge className="ml-2">{cloudResourceOwnershipRequests.length}</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Approve or reject requests from developers to own cloud resources.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {cloudResourceOwnershipRequests.map((request) => (
                            <div key={request.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-lg">
                              <div className="min-w-0">
                                <p className="font-medium">{request.user_email}</p>
                                <p className="text-sm text-muted-foreground">
                                  wants to own <strong>{request.resource_name || 'Unknown Resource'}</strong> ({request.resource_type})
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Requested on {new Date(request.created_at * 1000).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button size="sm" onClick={() => handleApproveCloudResourceOwnership(request.id)}>Approve</Button>
                                <Button size="sm" variant="destructive" onClick={() => handleRejectCloudResourceOwnership(request.id)}>Reject</Button>
                              </div>
                            </div>
                          ))}
                          {cloudResourceOwnershipRequests.length === 0 && (
                            <p className="text-muted-foreground text-center py-4">No pending cloud resource ownership requests.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {activeNpAdminTab === 'cloud-resources' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base font-medium">
                          Assign Cloud Resources to Users
                        </CardTitle>
                        <CardDescription>
                          Select a user, then assign cloud resources they are responsible for.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-6">
                        {assignableUsers.length === 0 ? (
                          <div className="text-center py-8">
                            <p className="text-muted-foreground">No assignable users found</p>
                            <p className="text-sm text-muted-foreground">Invite users with the App Developer or Platform Admin role to start assigning cloud resources.</p>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {/* User selection */}
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm flex items-center gap-2">
                                  Select User
                                </h4>
                                <Badge variant="secondary" className="text-xs">
                                  {filteredCloudDevelopers.length} of {assignableUsers.length} users
                                </Badge>
                              </div>

                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search developers..."
                                  value={cloudDeveloperSearchTerm}
                                  onChange={(e) => setCloudDeveloperSearchTerm(e.target.value)}
                                  className="pl-10"
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {filteredCloudDevelopers.map((dev) => (
                                  <div
                                    key={dev.user_id}
                                    onClick={() => setSelectedDeveloperForCloudResources(dev.user_id)}
                                    className={`group cursor-pointer p-4 border-2 rounded-lg transition-all ${
                                      selectedDeveloperForCloudResources === dev.user_id
                                        ? 'border-primary bg-primary/5 shadow-md'
                                        : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {dev.picture_url ? (
                                        <UserAvatar email={dev.email} pictureUrl={dev.picture_url} givenName={dev.given_name} familyName={dev.family_name} size="lg" />
                                      ) : (
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                          selectedDeveloperForCloudResources === dev.user_id
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground'
                                        }`}>
                                          <Code className="h-5 w-5" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium break-words text-sm" title={dev.email}>{dev.email}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {(cloudResourceAssignments[dev.user_id] || []).length} cloud resource(s)
                                        </p>
                                      </div>
                                      {selectedDeveloperForCloudResources === dev.user_id && (
                                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Cloud resource assignment - two column layout */}
                            {selectedDeveloperForCloudResources && (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6 border-t">
                                <div className="flex flex-col gap-4">
                                  <h4 className="font-medium text-sm">Available Cloud Resources</h4>
                                  <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                      <Input
                                        placeholder="Search cloud resources..."
                                        value={cloudFilter.searchTerm}
                                        onChange={(e) => cloudFilter.setSearchTerm(e.target.value)}
                                        className="pl-10"
                                      />
                                    </div>
                                    <CategoryFilterDropdown
                                      variant="popover"
                                      selectedCategories={cloudFilter.selectedCategories}
                                      onCategoryChange={cloudFilter.handleCategoryChange}
                                      onToggleAll={cloudFilter.toggleAllCategories}
                                    />
                                  </div>

                                  {(cloudFilter.vpcs.length > 0 || availableCloudResources.length > 0) && (
                                    <div className="text-sm text-muted-foreground">
                                      {cloudFilter.loading ? 'Loading...' : `${cloudFilter.showVpcGroups ? availableVpcs.length + ' VPCs, ' : ''}${availableCloudResources.length} resources`}
                                    </div>
                                  )}

                                  <div className="grid grid-cols-1 gap-3 flex-1 min-h-0 max-h-[500px] overflow-y-auto p-2 border rounded-lg bg-muted/20">
                                    {/* VPC groups */}
                                    {cloudFilter.showVpcGroups && availableVpcs.length > 0 && availableVpcs.map(vpc => (
                                      <div
                                        key={vpc.vpc_id}
                                        onClick={() => handleAssignVPC(selectedDeveloperForCloudResources, vpc.vpc_id)}
                                        className="group relative p-3 border-2 border-dashed border-blue-500/30 hover:border-blue-500/60 hover:bg-blue-500/5 hover:shadow-sm rounded-lg transition-all cursor-pointer"
                                      >
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                              <Network className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                              <p className="font-medium break-words">{vpc.vpc_name || vpc.vpc_id}</p>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1 break-words">
                                              {vpc.vpc_id} · {vpc.region} · {vpc.cloud_provider?.toUpperCase()}
                                              {vpc.aws_account_id && ` · ${vpc.aws_account_id}`}
                                            </p>
                                          </div>
                                          <Badge variant="secondary" className="text-xs flex-shrink-0">
                                            {vpc.resource_count} resources
                                          </Badge>
                                        </div>
                                      </div>
                                    ))}
                                    {/* Individual resources */}
                                    {availableCloudResources.map(res => (
                                      <div
                                        key={res.id}
                                        onClick={() => handleAssignCloudResource(selectedDeveloperForCloudResources, res.id)}
                                        className="group relative p-3 border-2 border-dashed border-primary/30 hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm rounded-lg transition-all cursor-pointer"
                                      >
                                        <div>
                                          <p className="font-medium break-words">{res.name || res.arn || 'Unnamed Resource'}</p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {res.resource_type}
                                          </p>
                                          {res.arn && res.name && (
                                            <p className="text-xs text-muted-foreground/70 break-words mt-1">{res.arn}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                    {!cloudFilter.loading && availableCloudResources.length === 0 && (!cloudFilter.showVpcGroups || availableVpcs.length === 0) && (
                                      <p className="text-muted-foreground text-center py-4">No cloud resources found matching your filters.</p>
                                    )}
                                  </div>
                                </div>

                                <div className="flex flex-col gap-4">
                                  <h4 className="font-medium text-sm">
                                    {users.find(u => u.user_id === selectedDeveloperForCloudResources)?.email}'s Cloud Resources
                                  </h4>
                                  <div className="space-y-2 flex-1 min-h-0 max-h-[500px] overflow-y-auto">
                                    {(cloudResourceAssignments[selectedDeveloperForCloudResources] || []).length > 0 ? (
                                      (() => {
                                        const assignedMappings = cloudResourceAssignments[selectedDeveloperForCloudResources] || [];
                                        const vpcGroups: Record<string, typeof assignedMappings> = {};
                                        const standalone: typeof assignedMappings = [];
                                        assignedMappings.forEach(m => {
                                          if (m.vpc_id) {
                                            if (!vpcGroups[m.vpc_id]) vpcGroups[m.vpc_id] = [];
                                            vpcGroups[m.vpc_id].push(m);
                                          } else {
                                            standalone.push(m);
                                          }
                                        });
                                        const toggleVpcCollapse = (vpcId: string) => {
                                          setCollapsedVpcs(prev => {
                                            const next = new Set(prev);
                                            if (next.has(vpcId)) next.delete(vpcId);
                                            else next.add(vpcId);
                                            return next;
                                          });
                                        };
                                        return (
                                          <div className="space-y-2">
                                            {Object.entries(vpcGroups).map(([vpcId, mappings]) => {
                                              const vpc = cloudFilter.vpcs.find(v => v.vpc_id === vpcId);
                                              const isCollapsed = collapsedVpcs.has(vpcId);
                                              return (
                                                <div key={vpcId} className="border rounded-lg overflow-hidden">
                                                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-500/5 border-b">
                                                    <button
                                                      onClick={() => toggleVpcCollapse(vpcId)}
                                                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                                                    >
                                                      {isCollapsed ? (
                                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                      ) : (
                                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                      )}
                                                      <Network className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                                                      <span className="text-sm font-medium truncate">{vpc?.vpc_name || vpcId}</span>
                                                      <Badge variant="secondary" className="text-xs flex-shrink-0">{mappings.length}</Badge>
                                                    </button>
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => handleRemoveVPC(selectedDeveloperForCloudResources, vpcId)}
                                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-7 text-xs flex-shrink-0"
                                                    >
                                                      Remove VPC
                                                    </Button>
                                                  </div>
                                                  {!isCollapsed && (
                                                    <div className="space-y-1 p-2">
                                                      {mappings.map(m => (
                                                        <div key={m.cloud_resource_id} className="group flex items-center justify-between p-2 rounded hover:bg-muted/50 transition-colors">
                                                          <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium break-words">{m.resource_name || m.resource_arn || 'Unnamed Resource'}</p>
                                                            <p className="text-xs text-muted-foreground">{m.resource_type}</p>
                                                          </div>
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveCloudResourceAssignment(selectedDeveloperForCloudResources, m.cloud_resource_id)}
                                                            className="sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0 ml-2 h-7 w-7 p-0"
                                                          >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                          </Button>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                            {standalone.map(m => (
                                              <div key={m.cloud_resource_id} className="border rounded-lg overflow-hidden">
                                                <div className="group flex items-center justify-between gap-2 px-3 py-2">
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium break-words">{m.resource_name || m.resource_arn || 'Unnamed Resource'}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                      {m.resource_type} ({m.cloud_provider?.toUpperCase()})
                                                    </p>
                                                    {m.resource_arn && m.resource_name && (
                                                      <p className="text-xs text-muted-foreground/70 break-words mt-0.5">{m.resource_arn}</p>
                                                    )}
                                                  </div>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveCloudResourceAssignment(selectedDeveloperForCloudResources, m.cloud_resource_id)}
                                                    className="sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0 ml-2 h-7 w-7 p-0"
                                                  >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                  </Button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()
                                    ) : (
                                      <div className="text-center py-8 text-muted-foreground">
                                        <p className="text-sm">No cloud resources assigned yet</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

              </div>
            </div>
          )}
          {activeMainTab === 'sso' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Okta SSO Configuration
                  </CardTitle>
                  <CardDescription>
                    Configure Single Sign-On and user provisioning with Okta for your organization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingOkta ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Tabs defaultValue="oidc" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="oidc">OIDC (SSO)</TabsTrigger>
                        <TabsTrigger value="scim">
                          <span className="sm:hidden">SCIM</span>
                          <span className="hidden sm:inline">SCIM (User Provisioning)</span>
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="oidc" className="space-y-4">
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-medium">OpenID Connect Configuration</h3>
                            {!isEditingOIDC && oktaConfig?.oidc_app && (
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsEditingOIDC(true)}
                                >
                                  Edit Configuration
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={handleDeleteOIDCConfig}
                                  disabled={loading}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {isEditingOIDC ? (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="okta-domain">Okta Domain</Label>
                                <Input
                                  id="okta-domain"
                                  placeholder="dev-12345.okta.com"
                                  value={oidcConfig.okta_domain}
                                  onChange={(e) => setOidcConfig({ ...oidcConfig, okta_domain: e.target.value })}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="client-id">Client ID</Label>
                                <Input
                                  id="client-id"
                                  placeholder="0oa..."
                                  value={oidcConfig.client_id}
                                  onChange={(e) => setOidcConfig({ ...oidcConfig, client_id: e.target.value })}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="client-secret">Client Secret</Label>
                                <div className="relative">
                                  <Input
                                    id="client-secret"
                                    type={showClientSecret ? 'text' : 'password'}
                                    placeholder="Enter client secret"
                                    value={oidcConfig.client_secret || ''}
                                    onChange={(e) => setOidcConfig({ ...oidcConfig, client_secret: e.target.value })}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                                    onClick={() => setShowClientSecret(!showClientSecret)}
                                  >
                                    {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </Button>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {oktaConfig?.oidc_app ? 'Leave empty to keep existing secret' : 'Required for initial setup'}
                                </p>
                              </div>

                              <div className="space-y-2">
                                <Label>Allowed Email Domains</Label>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="example.com"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                                  />
                                  <Button type="button" onClick={handleAddDomain} size="sm">
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {oidcConfig.allowed_domains.map((domain) => (
                                    <Badge key={domain} variant="secondary" className="px-3 py-1">
                                      {domain}
                                      <button
                                        onClick={() => handleRemoveDomain(domain)}
                                        className="ml-2 hover:text-destructive"
                                      >
                                        ×
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <Button onClick={handleSaveOIDCConfig} disabled={loading}>
                                  {loading ? 'Saving...' : 'Save Configuration'}
                                </Button>
                                <Button variant="outline" onClick={() => setIsEditingOIDC(false)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : oktaConfig?.oidc_app ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">Okta Domain</p>
                                  <p className="font-mono text-sm">{oktaConfig.oidc_app.okta_domain}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">Client ID</p>
                                  <div className="flex items-center gap-2">
                                    <p className="font-mono text-sm">{oktaConfig.oidc_app.client_id}</p>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyToClipboard(oktaConfig.oidc_app?.client_id || '')}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Redirect URI</p>
                                <div className="flex items-center gap-2">
                                  <p className="font-mono text-sm">{oktaConfig.oidc_app.redirect_uri}</p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyToClipboard(oktaConfig.oidc_app?.redirect_uri || '')}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>

                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-2">Allowed Domains</p>
                                <div className="flex flex-wrap gap-2">
                                  {oktaConfig.oidc_app.allowed_domains.map((domain) => (
                                    <Badge key={domain} variant="outline">
                                      {domain}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <p className="text-muted-foreground mb-4">No OIDC configuration found</p>
                              <Button onClick={() => setIsEditingOIDC(true)}>
                                Setup OIDC Configuration
                              </Button>
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="scim" className="space-y-4">
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-medium">SCIM Configuration</h3>
                            {!isEditingSCIM && oktaConfig?.scim_app && (
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsEditingSCIM(true)}
                                >
                                  Edit Configuration
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={handleDeleteSCIMConfig}
                                  disabled={loading}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {isEditingSCIM ? (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="scim-domain">Okta Domain</Label>
                                <Input
                                  id="scim-domain"
                                  placeholder="dev-12345.okta.com"
                                  value={scimConfig.okta_domain}
                                  onChange={(e) => setScimConfig({ ...scimConfig, okta_domain: e.target.value })}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="scim-app-name">SCIM App Name</Label>
                                <Input
                                  id="scim-app-name"
                                  placeholder="Kestrel AI SCIM Provisioning"
                                  value={scimConfig.scim_app_name}
                                  onChange={(e) => setScimConfig({ ...scimConfig, scim_app_name: e.target.value })}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="scim-token">Bearer Token</Label>
                                <div className="flex gap-2">
                                  <div className="relative flex-1">
                                    <Input
                                      id="scim-token"
                                      type={showSCIMToken ? 'text' : 'password'}
                                      placeholder="Enter bearer token or generate one"
                                      value={scimConfig.scim_token || ''}
                                      onChange={(e) => setScimConfig({ ...scimConfig, scim_token: e.target.value })}
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                                      onClick={() => setShowSCIMToken(!showSCIMToken)}
                                    >
                                      {showSCIMToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={generateBearerToken}
                                    className="whitespace-nowrap"
                                  >
                                    Generate Token
                                  </Button>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {oktaConfig?.scim_app 
                                    ? 'Leave empty to keep existing token, or generate a new secure token' 
                                    : 'Generate a secure bearer token that Okta will use to authenticate SCIM requests'}
                                </p>
                              </div>

                              <div className="flex gap-2">
                                <Button onClick={handleSaveSCIMConfig} disabled={loading}>
                                  {loading ? 'Saving...' : 'Save Configuration'}
                                </Button>
                                <Button variant="outline" onClick={() => setIsEditingSCIM(false)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : oktaConfig?.scim_app ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">Okta Domain</p>
                                  <p className="font-mono text-sm">{oktaConfig.scim_app.okta_domain}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">SCIM App Name</p>
                                  <p className="font-mono text-sm">{oktaConfig.scim_app.scim_app_name}</p>
                                </div>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">SCIM Base URL</p>
                                <div className="flex items-center gap-2">
                                  <p className="font-mono text-sm">{oktaConfig.scim_app.scim_base_url}</p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyToClipboard(oktaConfig.scim_app?.scim_base_url || '')}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>

                              <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                  Bearer token is securely stored and cannot be displayed. Update the configuration to set a new token.
                                </AlertDescription>
                              </Alert>
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <p className="text-muted-foreground mb-4">No SCIM configuration found</p>
                              <Button onClick={() => setIsEditingSCIM(true)}>
                                Setup SCIM Configuration
                              </Button>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  )}
                </CardContent>
              </Card>

              <SSOSetupWizard getRedirectUri={getRedirectUri} getSCIMBaseUrl={getSCIMBaseUrl} copyToClipboard={copyToClipboard} />
            </div>
          )}
          {activeMainTab === 'audit' && (
            <div className="pt-4">
              <AuditLog />
            </div>
          )}
        </div>
        
        <AlertDialog open={removeDialogOpen} onOpenChange={(open) => {
          setRemoveDialogOpen(open);
          if (!open) setUserToRemove(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. <strong>"{userToRemove?.email}"</strong> will be removed from the organization and lose access to all resources immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemoveUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={roleDialogOpen} onOpenChange={(open) => {
        setRoleDialogOpen(open);
        if (!open) setUserToEdit(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5 text-muted-foreground" />
              Change User Role
            </AlertDialogTitle>
            <AlertDialogDescription>
              Update the role for <strong>"{userToEdit?.email}"</strong>. This will immediately change their access permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="space-y-3">
              <Label htmlFor="role-select">Select New Role</Label>
              <Select value={newRole} onValueChange={(value: 'app_developer' | 'ops_admin' | 'org_admin') => setNewRole(value)}>
                <SelectTrigger id="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" side="bottom" align="start">
                  <SelectItem value="app_developer">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-muted-foreground" />
                      App Developer
                    </div>
                  </SelectItem>
                  <SelectItem value="ops_admin">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      Platform Admin
                    </div>
                  </SelectItem>
                  <SelectItem value="org_admin">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-muted-foreground" />
                      Organization Admin
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              {userToEdit && (
                <div className="text-sm text-muted-foreground">
                  <p><strong>Current role:</strong> {formatRole(userToEdit.currentRole)}</p>
                  <p><strong>New role:</strong> {formatRole(newRole)}</p>
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRoleChange}
              disabled={loading || newRole === userToEdit?.currentRole}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {loading ? 'Updating...' : 'Update Role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamManagement;

// ---------------------------------------------------------------------------
// User Groups Tab
// ---------------------------------------------------------------------------

function UserGroupsTab({ users }: { users: User[] }) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [members, setMembers] = useState<UserGroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<UserGroup | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      const data = await apiClient.listUserGroups();
      setGroups(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadMembers = async (group: UserGroup) => {
    setSelectedGroup(group);
    setMembersLoading(true);
    try {
      const m = await apiClient.listUserGroupMembers(group.id);
      setMembers(m || []);
    } catch { /* ignore */ } finally { setMembersLoading(false); }
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      await apiClient.createUserGroup(createName.trim(), createDesc.trim());
      setShowCreateDialog(false);
      setCreateName('');
      setCreateDesc('');
      loadGroups();
    } catch (err: any) {
      window.alert(err?.response?.data || 'Failed to create group');
    }
  };

  const handleUpdate = async () => {
    if (!editingGroup || !editName.trim()) return;
    try {
      await apiClient.updateUserGroup(editingGroup.id, editName.trim(), editDesc.trim());
      setEditingGroup(null);
      loadGroups();
      if (selectedGroup?.id === editingGroup.id) {
        setSelectedGroup({ ...selectedGroup, name: editName.trim(), description: editDesc.trim() });
      }
    } catch (err: any) {
      window.alert(err?.response?.data || 'Failed to update group');
    }
  };

  const handleDelete = (group: UserGroup) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      await apiClient.deleteUserGroup(groupToDelete.id);
      if (selectedGroup?.id === groupToDelete.id) {
        setSelectedGroup(null);
        setMembers([]);
      }
      loadGroups();
    } catch { window.alert('Failed to delete group'); }
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const handleAddMembers = async (userIds: string[]) => {
    if (!selectedGroup || userIds.length === 0) return;
    await apiClient.addUserGroupMembers(selectedGroup.id, userIds);
    loadMembers(selectedGroup);
    loadGroups();
    setAddMemberOpen(false);
    setMemberQuery('');
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;
    await apiClient.removeUserGroupMember(selectedGroup.id, userId);
    setMembers(prev => prev.filter(m => m.user_id !== userId));
    loadGroups();
  };

  const memberUserIds = new Set(members.map(m => m.user_id));
  const availableUsers = users.filter(u => !memberUserIds.has(u.user_id));
  const filteredAvailable = availableUsers.filter(u =>
    u.email.toLowerCase().includes(memberQuery.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">Loading user groups...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            User Groups
            <Badge variant="secondary" className="ml-auto">
              {groups.length}
            </Badge>
          </CardTitle>
          <CardDescription>
            Create user groups and assign them to workflow approval gates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-end">
            <Button onClick={() => setShowCreateDialog(true)} size="sm">
              <Plus className="w-4 h-4 mr-1" /> New Group
            </Button>
          </div>

          {showCreateDialog && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div>
                  <label className="text-xs font-medium">Group Name</label>
                  <Input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. Cloud Infrastructure" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium">Description</label>
                  <Input value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="Optional description" className="mt-1" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreate} disabled={!createName.trim()}>Create</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Groups list */}
        <div className="space-y-2">
          {groups.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-sm text-muted-foreground min-h-[120px] flex items-center justify-center">No user groups yet.</CardContent></Card>
          ) : groups.map(g => (
            <Card
              key={g.id}
              className={`cursor-pointer transition-colors hover:border-primary/40 ${selectedGroup?.id === g.id ? 'border-primary' : ''}`}
              onClick={() => loadMembers(g)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{g.name}</div>
                    {g.description && <div className="text-xs text-muted-foreground truncate">{g.description}</div>}
                    <div className="text-xs text-muted-foreground mt-0.5">{g.member_count} member{g.member_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditingGroup(g); setEditName(g.name); setEditDesc(g.description); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={e => { e.stopPropagation(); handleDelete(g); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Group detail / members */}
        <div className="lg:col-span-2">
          {editingGroup && (
            <Card className="mb-4">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-medium">Edit Group</h3>
                <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Group name" />
                <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleUpdate} disabled={!editName.trim()}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedGroup ? (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{selectedGroup.name} — Members</h3>
                  <Button size="sm" variant="outline" onClick={() => setAddMemberOpen(!addMemberOpen)}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Member
                  </Button>
                </div>

                {addMemberOpen && (
                  <div className="border rounded-md p-3 space-y-2">
                    <Input
                      value={memberQuery}
                      onChange={e => setMemberQuery(e.target.value)}
                      placeholder="Search users by email..."
                      className="h-8 text-sm"
                    />
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {filteredAvailable.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2 text-center">No users available</p>
                      ) : filteredAvailable.slice(0, 10).map(u => (
                        <button
                          key={u.user_id}
                          onClick={() => handleAddMembers([u.user_id])}
                          className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                        >
                          {u.email}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {membersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading members...</span>
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No members yet. Add users above.</p>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">User</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Role</th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => (
                          <tr key={m.user_id} className="border-b last:border-0">
                            <td className="px-3 py-2">
                              <div className="font-medium">{m.name}</div>
                              {m.name !== m.email && <div className="text-xs text-muted-foreground">{m.email}</div>}
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{m.role}</span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Button variant="ghost" size="sm" className="h-7 text-destructive text-xs" onClick={() => handleRemoveMember(m.user_id)}>
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-10 text-center text-sm text-muted-foreground min-h-[120px] flex items-center justify-center">
                Select a group to manage its members
              </CardContent>
            </Card>
          )}
        </div>
      </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setGroupToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Group?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The group <strong>&ldquo;{groupToDelete?.name}&rdquo;</strong> will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Access Control Tab
// ---------------------------------------------------------------------------

const ALL_PAGES = [
  { key: 'all_workflows', label: 'All Workflows' },
  { key: 'executions', label: 'Executions' },
  { key: 'library', label: 'Library' },
  { key: 'pending_approvals', label: 'Pending Approvals' },
  { key: 'workflow_requests', label: 'Workflow Requests' },
  { key: 'observability', label: 'Observability' },
  { key: 'api_keys', label: 'API Keys' },
  { key: 'developer_requests', label: 'Developer Requests' },
];

const ALL_API_SCOPES = [
  { value: '*', label: 'Full Access (all scopes)' },
  { value: 'workflows:read', label: 'Workflows: Read' },
  { value: 'workflows:write', label: 'Workflows: Write' },
  { value: 'workflows:activate', label: 'Workflows: Activate' },
  { value: 'workflows:delete', label: 'Workflows: Delete' },
  { value: 'executions:read', label: 'Executions: Read' },
  { value: 'executions:cancel', label: 'Executions: Cancel' },
  { value: 'approvals:read', label: 'Approvals: Read' },
  { value: 'approvals:manage', label: 'Approvals: Manage' },
  { value: 'requests:read', label: 'Requests: Read' },
  { value: 'requests:manage', label: 'Requests: Manage' },
  { value: 'catalog:read', label: 'Catalog: Read' },
];

function AccessControlTab() {
  const [configs, setConfigs] = useState<Record<string, { page_access: string[]; api_key_scopes: string[] }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [workflowRequestsInfoOpen, setWorkflowRequestsInfoOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await apiClient.getRoleAccessConfig();
        setConfigs(data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const togglePage = (role: string, pageKey: string) => {
    setConfigs(prev => {
      const cfg = prev[role] || { page_access: [], api_key_scopes: [] };
      const pages = cfg.page_access.includes(pageKey)
        ? cfg.page_access.filter(p => p !== pageKey)
        : [...cfg.page_access, pageKey];
      return { ...prev, [role]: { ...cfg, page_access: pages } };
    });
  };

  const toggleScope = (role: string, scope: string) => {
    setConfigs(prev => {
      const cfg = prev[role] || { page_access: [], api_key_scopes: [] };
      let scopes: string[];
      if (scope === '*') {
        scopes = cfg.api_key_scopes.includes('*') ? [] : ['*'];
      } else {
        scopes = cfg.api_key_scopes.filter(s => s !== '*');
        scopes = scopes.includes(scope) ? scopes.filter(s => s !== scope) : [...scopes, scope];
      }
      return { ...prev, [role]: { ...cfg, api_key_scopes: scopes } };
    });
  };

  const saveRole = async (role: string) => {
    setSaving(role);
    try {
      const cfg = configs[role];
      await apiClient.updateRoleAccessConfig(role, cfg.page_access, cfg.api_key_scopes);
    } catch { /* silent */ }
    setSaving(null);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            Access Control
          </CardTitle>
          <CardDescription>
            Configure which Workflow pages and API key scopes each role can access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {(['ops_admin', 'app_developer'] as const).map(role => {
            const cfg = configs[role] || { page_access: [], api_key_scopes: [] };
            const roleLabel = role === 'ops_admin' ? 'Platform Admin' : 'App Developer';
            return (
              <div key={role} className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-sm font-semibold">{roleLabel}</h3>
                  <Button size="sm" onClick={() => saveRole(role)} disabled={saving === role}>
                    {saving === role ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>

                <div className="flex gap-0 max-md:flex-col max-md:gap-6">
                  <div className="flex-1 space-y-2">
                    <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Visible Pages</h4>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0 max-md:grid-cols-1">
                      {ALL_PAGES.map(page => {
                        const checked = cfg.page_access.includes(page.key);
                        return (
                          <label key={page.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer transition-colors">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => togglePage(role, page.key)}
                            />
                            <span className="text-sm">{page.label}</span>
                            {role === 'app_developer' && page.key === 'workflow_requests' && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWorkflowRequestsInfoOpen(true); }}
                                className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="w-px bg-border mx-4 self-stretch max-md:hidden" />

                  <div className="flex-1 space-y-2">
                    <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Allowed API Key Scopes</h4>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0 max-md:grid-cols-1">
                      {ALL_API_SCOPES.map(scope => {
                        const isFullAccess = cfg.api_key_scopes.includes('*');
                        const checked = isFullAccess || cfg.api_key_scopes.includes(scope.value);
                        return (
                          <label key={scope.value} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer transition-colors ${scope.value !== '*' && isFullAccess ? 'opacity-50' : ''}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleScope(role, scope.value)}
                              disabled={scope.value !== '*' && isFullAccess}
                            />
                            <span className="text-sm">{scope.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <AlertDialog open={workflowRequestsInfoOpen} onOpenChange={setWorkflowRequestsInfoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Workflow Requests (App Developer)</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                When enabled for app developers, this provides a <strong>read-only</strong> view of the Workflow Requests page that shows only the developer's own requests.
              </p>
              <p>
                App developers can see the status of their requests (pending, executing, completed, failed) but cannot approve, reject, or manage other users' requests. Admins retain full access to all requests and actions.
              </p>
              <p className="text-xs text-muted-foreground">
                Requests are submitted via the Developer Request Agent chat, Slack <code>/kestrel-workflow</code>, or the CLI.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}